import React, { useState } from 'react';
import {
  AppBar, Toolbar, Typography, IconButton, Drawer, List, ListItem,
  ListItemButton, ListItemIcon, ListItemText, Box, Badge, Tooltip,
  Avatar, Menu, MenuItem, Divider, useTheme, useMediaQuery,
  Chip,
} from '@mui/material';
import {
  Menu as MenuIcon, Dashboard, FolderOpen, Send, Block,
  HelpOutline, Schedule, EmojiEvents, Cancel,
  Notifications, Logout, Assessment,
  FileUpload, FileDownload, Settings,
  Storage, ContactPhone, SmartToy,
} from '@mui/icons-material';
import { useTenders } from '../../hooks/TenderContext';
import { useAuth, ROLE_LABELS } from '../../hooks/AuthContext';

const DRAWER_WIDTH = 260;

const MENU_ITEMS = [
  { key: 'active', label: 'מכרזים פעילים', icon: <FolderOpen />, color: '#1565C0' },
  { key: 'submitted', label: 'הוגשו', icon: <Send sx={{ transform: 'scaleX(-1)' }} />, color: '#2E7D32' },
  { key: 'skipped', label: 'לא ניגשנו', icon: <Block />, color: '#757575' },
  { key: 'clarification', label: 'שאלות הבהרה', icon: <HelpOutline />, color: '#FF6F00' },
  { key: 'upcoming', label: 'הגשה לטיפול', icon: <Schedule />, color: '#F57F17' },
  { key: 'won', label: 'זכיות', icon: <EmojiEvents />, color: '#2E7D32' },
  { key: 'lost', label: 'אי-זכיות', icon: <Cancel />, color: '#C62828' },
  { key: 'projects_db', label: 'מאגר מידע פרויקטים', icon: <Storage />, color: '#5C6BC0' },
  { key: 'contacts_db', label: 'מאגר אנשי מפתח', icon: <ContactPhone />, color: '#00897B' },
  { key: 'ai_agent', label: 'עוזר AI', icon: <SmartToy />, color: '#1565C0' },
];

const Layout = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { selectedCategory, setSelectedCategory, stats, alerts } = useTenders();
  const { currentUser, hasPermission, logout } = useAuth();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [notifAnchor, setNotifAnchor] = useState(null);
  const [userAnchor, setUserAnchor] = useState(null);

  const unreadAlerts = alerts.filter(a => a.level === 'critical').length;
  const getStatCount = (key) => stats[key] || 0;

  const handleNavClick = (key) => {
    setSelectedCategory(key);
    if (isMobile) setMobileOpen(false);
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin': return '#C62828';
      case 'editor': return '#FF6F00';
      case 'viewer': return '#1565C0';
      default: return '#757575';
    }
  };

  const sidebarContent = (
    <Box sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      bgcolor: '#fff',
    }}>
      {/* ===== Fix #1: FIXED LOGO - white background, not changeable ===== */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 90,
          bgcolor: '#fff',
          borderBottom: `1px solid ${theme.palette.divider}`,
          px: 2,
        }}
      >
        <img
          src={process.env.PUBLIC_URL + '/logo.svg'}
          alt="WXG Logo"
          style={{
            maxWidth: '180px',
            maxHeight: '60px',
            objectFit: 'contain',
          }}
        />
      </Box>

      {/* ===== Fix #2: Only "מערכת ניהול מכרזים" without TMS ===== */}
      <Box sx={{
        px: 2, py: 1.5,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        borderBottom: `1px solid ${theme.palette.divider}`,
      }}>
        <Assessment sx={{ color: theme.palette.primary.main, fontSize: 28 }} />
        <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.2 }}>
          מערכת ניהול מכרזים
        </Typography>
      </Box>

      {/* Navigation */}
      <List sx={{ flex: 1, px: 1, py: 1, overflowY: 'auto' }}>
        <ListItem disablePadding sx={{ mb: 0.5 }}>
          <ListItemButton
            selected={selectedCategory === 'all'}
            onClick={() => handleNavClick('all')}
            sx={{
              borderRadius: 2, mx: 0.5,
              '&.Mui-selected': {
                bgcolor: theme.palette.primary.main + '15',
                '&:hover': { bgcolor: theme.palette.primary.main + '25' },
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <Dashboard sx={{ color: selectedCategory === 'all' ? theme.palette.primary.main : 'inherit' }} />
            </ListItemIcon>
            <ListItemText
              primary="דשבורד ראשי"
              primaryTypographyProps={{ fontWeight: selectedCategory === 'all' ? 600 : 400 }}
            />
          </ListItemButton>
        </ListItem>

        <Divider sx={{ my: 1 }} />

        {MENU_ITEMS.map(item => (
          <ListItem key={item.key} disablePadding sx={{ mb: 0.3 }}>
            <ListItemButton
              selected={selectedCategory === item.key}
              onClick={() => handleNavClick(item.key)}
              sx={{
                borderRadius: 2, mx: 0.5,
                '&.Mui-selected': {
                  bgcolor: item.color + '15',
                  borderRight: `3px solid ${item.color}`,
                  '&:hover': { bgcolor: item.color + '25' },
                },
              }}
            >
              <ListItemIcon sx={{
                minWidth: 40,
                color: selectedCategory === item.key ? item.color : 'inherit',
              }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{
                  fontWeight: selectedCategory === item.key ? 600 : 400,
                  fontSize: '0.875rem',
                }}
              />
              <Badge
                badgeContent={getStatCount(item.key)}
                max={999}
                sx={{
                  '& .MuiBadge-badge': {
                    fontSize: '0.7rem',
                    minWidth: 22, height: 22, borderRadius: 11,
                    bgcolor: selectedCategory === item.key ? item.color : theme.palette.grey[200],
                    color: selectedCategory === item.key ? '#fff' : theme.palette.text.secondary,
                  },
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}

        {/* ===== Fix #5: Settings in sidebar ===== */}
        {hasPermission('settings:manage') && (
          <>
            <Divider sx={{ my: 1 }} />
            <ListItem disablePadding sx={{ mb: 0.3 }}>
              <ListItemButton
                selected={selectedCategory === 'settings'}
                onClick={() => handleNavClick('settings')}
                sx={{
                  borderRadius: 2, mx: 0.5,
                  '&.Mui-selected': {
                    bgcolor: '#607D8B15',
                    borderRight: '3px solid #607D8B',
                    '&:hover': { bgcolor: '#607D8B25' },
                  },
                }}
              >
                <ListItemIcon sx={{
                  minWidth: 40,
                  color: selectedCategory === 'settings' ? '#607D8B' : 'inherit',
                }}>
                  <Settings />
                </ListItemIcon>
                <ListItemText
                  primary="הגדרות"
                  primaryTypographyProps={{
                    fontWeight: selectedCategory === 'settings' ? 600 : 400,
                    fontSize: '0.875rem',
                  }}
                />
              </ListItemButton>
            </ListItem>
          </>
        )}
      </List>

      {/* ===== Fix #6: User menu in sidebar bottom ===== */}
      <Box sx={{
        p: 2,
        borderTop: `1px solid ${theme.palette.divider}`,
        bgcolor: theme.palette.grey[50],
      }}>
        <Box
          onClick={(e) => setUserAnchor(e.currentTarget)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            cursor: 'pointer',
            p: 1,
            borderRadius: 2,
            transition: 'background 0.2s',
            '&:hover': { bgcolor: theme.palette.grey[200] },
          }}
        >
          <Avatar
            src={currentUser.photo || undefined}
            sx={{
              width: 36, height: 36,
              bgcolor: getRoleColor(currentUser.role),
              fontSize: '0.8rem',
            }}
          >
            {!currentUser.photo && (currentUser.name?.charAt(0) || 'מ')}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" fontWeight={600} noWrap>
              {currentUser.name}
            </Typography>
            <Chip
              label={ROLE_LABELS[currentUser.role]}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.65rem',
                fontWeight: 600,
                bgcolor: getRoleColor(currentUser.role) + '18',
                color: getRoleColor(currentUser.role),
                mt: 0.3,
              }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* ===== SIDEBAR - DESKTOP: Fixed on RIGHT ===== */}
      {/* Using inline style to bypass stylis RTL plugin flipping */}
      {!isMobile && (
        <Box
          component="nav"
          style={{
            position: 'fixed',
            top: 0,
            right: 0,
            bottom: 0,
            width: DRAWER_WIDTH,
            borderLeft: '1px solid rgba(0,0,0,0.12)',
            zIndex: 1201,
            overflowY: 'auto',
            backgroundColor: '#fff',
          }}
        >
          {sidebarContent}
        </Box>
      )}

      {/* ===== SIDEBAR - MOBILE: Drawer from RIGHT ===== */}
      {isMobile && (
        <Drawer
          variant="temporary"
          anchor="right"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{ '& .MuiDrawer-paper': { width: DRAWER_WIDTH } }}
        >
          {sidebarContent}
        </Drawer>
      )}

      {/* ===== MAIN CONTENT ===== */}
      {/* Using inline style for marginRight to bypass RTL flipping */}
      <Box
        style={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
          marginRight: isMobile ? 0 : DRAWER_WIDTH,
        }}
      >
        {/* Top Bar */}
        <AppBar
          position="sticky"
          elevation={0}
          sx={{
            bgcolor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Toolbar>
            {isMobile && (
              <IconButton onClick={() => setMobileOpen(true)} sx={{ ml: 1 }}>
                <MenuIcon />
              </IconButton>
            )}

            <Typography variant="h6" fontWeight={600} sx={{ flexGrow: 1 }}>
              {selectedCategory === 'all' ? 'דשבורד ראשי' :
                selectedCategory === 'settings' ? 'הגדרות' :
                MENU_ITEMS.find(m => m.key === selectedCategory)?.label || 'דשבורד'}
            </Typography>

            <Chip
              label={ROLE_LABELS[currentUser.role]}
              size="small"
              sx={{
                ml: 1,
                height: 24,
                fontSize: '0.7rem',
                fontWeight: 600,
                bgcolor: getRoleColor(currentUser.role) + '15',
                color: getRoleColor(currentUser.role),
                border: `1px solid ${getRoleColor(currentUser.role)}40`,
              }}
            />

            <Tooltip title="התראות">
              <IconButton onClick={(e) => setNotifAnchor(e.currentTarget)}>
                <Badge badgeContent={unreadAlerts} color="error">
                  <Notifications />
                </Badge>
              </IconButton>
            </Tooltip>
          </Toolbar>
        </AppBar>

        {/* Notifications Menu */}
        <Menu
          anchorEl={notifAnchor}
          open={Boolean(notifAnchor)}
          onClose={() => setNotifAnchor(null)}
          PaperProps={{ sx: { width: 360, maxHeight: 400 } }}
        >
          <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
            <Typography variant="subtitle1" fontWeight={600}>התראות</Typography>
          </Box>
          {alerts.length === 0 ? (
            <MenuItem disabled>
              <Typography variant="body2" color="text.secondary">אין התראות חדשות</Typography>
            </MenuItem>
          ) : (
            alerts.slice(0, 10).map(alert => (
              <MenuItem
                key={alert.id}
                onClick={() => setNotifAnchor(null)}
                sx={{
                  borderRight: `3px solid ${
                    alert.level === 'critical' ? '#C62828' :
                    alert.level === 'warning' ? '#FF6F00' : '#1565C0'
                  }`,
                  whiteSpace: 'normal', py: 1.5,
                }}
              >
                <Box>
                  <Typography variant="body2" fontWeight={500}>
                    {alert.type} - {alert.tenderSubject}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {alert.daysLeft < 0 ? `עבר לפני ${Math.abs(alert.daysLeft)} ימים` :
                     alert.daysLeft === 0 ? 'היום!' :
                     `בעוד ${alert.daysLeft} ימים`}
                  </Typography>
                </Box>
              </MenuItem>
            ))
          )}
        </Menu>

        {/* User Menu from sidebar */}
        <Menu
          anchorEl={userAnchor}
          open={Boolean(userAnchor)}
          onClose={() => setUserAnchor(null)}
          PaperProps={{ sx: { minWidth: 200 } }}
          anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${theme.palette.divider}` }}>
            <Typography variant="body2" fontWeight={600}>{currentUser.name}</Typography>
            <Typography variant="caption" color="text.secondary">{currentUser.email}</Typography>
          </Box>
          {hasPermission('data:import') && (
            <MenuItem onClick={() => setUserAnchor(null)}>
              <ListItemIcon><FileUpload fontSize="small" /></ListItemIcon>
              <ListItemText>ייבוא מ-Excel</ListItemText>
            </MenuItem>
          )}
          <MenuItem onClick={() => setUserAnchor(null)}>
            <ListItemIcon><FileDownload fontSize="small" /></ListItemIcon>
            <ListItemText>ייצוא ל-Excel</ListItemText>
          </MenuItem>
          <Divider />
          <MenuItem onClick={() => { setUserAnchor(null); if (logout) logout(); }}>
            <ListItemIcon><Logout fontSize="small" /></ListItemIcon>
            <ListItemText>יציאה</ListItemText>
          </MenuItem>
        </Menu>

        {/* Page Content */}
        <Box component="main" sx={{ flex: 1, p: { xs: 2, md: 3 }, overflow: 'auto' }}>
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default Layout;
